/**
 * Save schema — the canonical shape of every saved game state.
 *
 * # The contract
 *
 * Every save is a `SaveEnvelope`: a JSON-serializable object containing the
 * full game state plus integrity metadata. This file is the single source of
 * truth for what a save looks like. Every other layer of the save system
 * (serialize, integrity, migrations, Phase B's adapter) imports from here.
 *
 * # Zod-first, types derived
 *
 * Schemas are defined as Zod schemas. TypeScript types are derived via
 * `z.infer<typeof Schema>`. This eliminates the most common save-system
 * footgun: runtime validation that drifts from compile-time types. With Zod
 * inference, a schema change automatically updates the type, and a type
 * misuse is a compile error before it can ship.
 *
 * # Strict objects
 *
 * Every object schema uses `.strict()` so unknown fields cause validation to
 * fail. This catches bugs in serialization (forgetting to strip a runtime
 * field), regressions in migrations (leaving a deprecated field), and
 * tampered saves (someone added a field the schema doesn't recognize).
 *
 * # V2 (current) vs V1 schemas
 *
 * The current save schemas (GameStateSchema, CombatStateSchema, etc.) reflect
 * the v2 shape — real-time isometric action-roguelite combat. V1's turn-based
 * card combat schemas remain in this file as `LegacyCombatStateV1Schema` and
 * `LegacyCombatantSchema`, used only by the v1→v2 migration to validate
 * its input. V1 schemas are NOT exported by the save layer's public barrel.
 *
 * # LocalState vs ServerState
 *
 * Two save scopes serve different purposes:
 *
 *   - `LocalState` — full client-side state, including UI preferences
 *     (selected tab, audio volume, hotbar layout) that don't need to sync
 *     to the server. Stored in IndexedDB only. Phase B writes this on
 *     every autosave.
 *
 *   - `ServerState` — authoritative subset that defines progression. This
 *     is what gets synced to api.aevonswake.com in Stage 1 for cloud saves
 *     and anti-cheat replay. Excludes UI preferences.
 *
 * `LocalState` is a superset of `ServerState`. The `gameState` field is
 * shared; `LocalState` adds `uiPrefs` on top.
 *
 * # Currency type seam
 *
 * `CurrencyValueSchema` currently validates as `z.number().finite()`. When
 * economy lands and break_eternity.js gets wired in, this single schema
 * flips to validating either a number (legacy v1 saves) or a string
 * (Decimal storage form). The schema is the single point of swap; downstream
 * code that imports `CurrencyValue` from here gets the new behavior for free.
 *
 * # Action log
 *
 * `actionLog` is an array of every action dispatched since the start of the
 * current run (or session — exact semantics TBD when run system lands).
 * Used for:
 *
 *   - Server-side replay validation (Stage 2 anti-cheat)
 *   - Bug reproduction (paste an action log into a test, replay deterministically)
 *   - Future feature: undo/redo (replay all actions up to step N)
 *
 * # SAVE_VERSION
 *
 * The envelope's `version` field drives the migration system. Currently 2.
 * When GameState gets a breaking change, bump SAVE_VERSION and author a
 * migration v_n → v_(n+1).
 *
 * SAVE_VERSION lives in state.ts (canonical) and is re-imported here for
 * the schema's default value. Single source of truth.
 *
 * @see {@link ./serialize.ts} — produces SaveEnvelope from GameState
 * @see {@link ./integrity.ts} — adds/verifies the checksum field
 * @see {@link ./migrations/index.ts} — upgrades older envelopes to current SAVE_VERSION
 * @see {@link ../currency.ts} — Currency type seam (future Decimal flip)
 * @see {@link ../actions/index.ts} — Action union for actionLog entries
 */

import { z } from 'zod';
import { SAVE_VERSION } from '../state';

// ─────────────────────────────────────────────────────────────────────────────
// Currency value seam
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Schema for a single currency value as stored in a save.
 *
 * v1: `z.number().finite()`. Native number, JSON-safe directly.
 * Future: `z.union([z.number(), z.string()])` — number is legacy, string is
 *         Decimal storage form.
 *
 * The `.finite()` constraint rejects NaN, Infinity, -Infinity. These should
 * never appear in a valid currency balance.
 */
export const CurrencyValueSchema = z.number().finite();
export type CurrencyValue = z.infer<typeof CurrencyValueSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// RNG snapshot
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Schema for SeededRng's serialized state. The s0/s1 fields are bigints
 * stringified for JSON safety.
 */
export const RngSnapshotSchema = z
  .object({
    s0: z.string().regex(/^-?\d+$/, 'must be a valid bigint string'),
    s1: z.string().regex(/^-?\d+$/, 'must be a valid bigint string'),
  })
  .strict();
export type RngSnapshotValidated = z.infer<typeof RngSnapshotSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// V2 — Identifier literal unions (mirror state/types.ts)
// ─────────────────────────────────────────────────────────────────────────────

/** Six AI factions per project canon. */
export const FactionIdSchema = z.enum([
  'compact',
  'severed',
  'cohort',
  'aevon_risen',
  'drift',
  'pale',
]);

/** Combat team allegiance: prime, any faction, or neutral. */
export const TeamIdSchema = z.enum([
  'prime',
  'compact',
  'severed',
  'cohort',
  'aevon_risen',
  'drift',
  'pale',
  'neutral',
]);

/** All 12 player Architectures. */
export const ArchitectureIdSchema = z.enum([
  'vector',
  'sentinel',
  'daemon',
  'cipher',
  'recursive',
  'quantum',
  'echo',
  'bastion',
  'plague',
  'resonant',
  'singularity',
  'wraith',
]);

/** Ability hotbar slot. */
export const AbilitySlotSchema = z.enum(['Q', 'E', 'R', 'F']);

/** Four biomes per project canon. */
export const BiomeIdSchema = z.enum([
  'compact',
  'drift',
  'aevon_risen',
  'severed',
]);

/** Six cosmetic palettes (default + 5 cosmetic variants). */
export const PaletteIdSchema = z.enum([
  'default',
  'crimson',
  'cyan_glitch',
  'compact_loyal',
  'aevon_corrupted',
  'pale_decay',
]);

/**
 * Ability identifier schema. Currently any non-empty string; tightens to a
 * literal union when the ability catalog is designed.
 */
export const AbilityIdSchema = z.string().min(1);

/**
 * Subroutine identifier schema. Currently any non-empty string; tightens to
 * a literal union when the Subroutine catalog is designed.
 */
export const SubroutineIdSchema = z.string().min(1);

/**
 * Branded ID schemas. The branding is compile-time only; at runtime these
 * are just non-empty strings. Length cap at 64 prevents pathological
 * adversarial inputs (DoS via gigabyte ID strings in tampered saves).
 */
const BrandedIdSchema = z.string().min(1).max(64);

export const EntityIdSchema = BrandedIdSchema;
export const ProjectileIdSchema = BrandedIdSchema;
export const EffectIdSchema = BrandedIdSchema;

// ─────────────────────────────────────────────────────────────────────────────
// V2 — Loadout
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Player's combat configuration. Replaces v1's deck concept.
 */
export const LoadoutSchema = z
  .object({
    architectureId: ArchitectureIdSchema,
    abilities: z
      .object({
        Q: AbilityIdSchema,
        E: AbilityIdSchema,
        R: AbilityIdSchema,
        F: AbilityIdSchema,
      })
      .strict(),
    /**
     * Tuple of exactly 3 Subroutine slots, each either an ID or null.
     * Zod's `.tuple` enforces fixed length; less-than-3 or more-than-3
     * fails validation.
     */
    subroutineSlots: z.tuple([
      SubroutineIdSchema.nullable(),
      SubroutineIdSchema.nullable(),
      SubroutineIdSchema.nullable(),
    ]),
    cosmeticPaletteId: PaletteIdSchema,
  })
  .strict();

export type LoadoutValidated = z.infer<typeof LoadoutSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// V2 — Tile map
// ─────────────────────────────────────────────────────────────────────────────

/** Spawn point category. */
export const SpawnPointKindSchema = z.enum([
  'player_start',
  'enemy_wave',
  'subroutine_deploy',
  'boss',
]);

export const SpawnPointSchema = z
  .object({
    tileX: z.number().int().nonnegative(),
    tileY: z.number().int().nonnegative(),
    kind: SpawnPointKindSchema,
  })
  .strict();

export const ExitZoneSchema = z
  .object({
    tileX: z.number().int().nonnegative(),
    tileY: z.number().int().nonnegative(),
    width: z.number().int().positive(),
    height: z.number().int().positive(),
    destinationId: z.string().min(1).max(128),
  })
  .strict();

export const HazardZoneKindSchema = z.enum([
  'damage_floor',
  'slow_field',
  'no_walk',
]);

export const HazardZoneSchema = z
  .object({
    tileX: z.number().int().nonnegative(),
    tileY: z.number().int().nonnegative(),
    width: z.number().int().positive(),
    height: z.number().int().positive(),
    kind: HazardZoneKindSchema,
  })
  .strict();

/**
 * Tile map schema.
 *
 * # Size limits — security
 *
 * Map dimensions are capped at 256 × 256 (65536 tiles). Tampered saves
 * declaring 100000 × 100000 maps would cause:
 *   - JSON arrays with 10 billion entries (memory exhaustion)
 *   - Infinite loops in collision/spatial queries (DoS)
 * The cap is well above any realistic combat arena size; even Vampire
 * Survivors-scale arenas fit easily within 256 × 256.
 *
 * The `tiles` and `collision` arrays must have length exactly width * height,
 * verified by `.refine()`.
 */
export const TileMapSchema = z
  .object({
    width: z.number().int().min(1).max(256),
    height: z.number().int().min(1).max(256),
    tileSize: z.number().int().min(1).max(4096),
    tiles: z.array(z.number().int().nonnegative()).max(65536),
    collision: z.array(z.boolean()).max(65536),
    spawnPoints: z.array(SpawnPointSchema).max(256),
    exits: z.array(ExitZoneSchema).max(64),
    hazards: z.array(HazardZoneSchema).max(256),
    biomeId: BiomeIdSchema,
    /** Generation seed as bigint string for JSON safety. */
    generationSeed: z.string().regex(/^-?\d+$/, 'must be a valid bigint string'),
  })
  .strict()
  .refine(
    (m) => m.tiles.length === m.width * m.height,
    {
      message: 'tiles array length must equal width × height',
      path: ['tiles'],
    },
  )
  .refine(
    (m) => m.collision.length === m.width * m.height,
    {
      message: 'collision array length must equal width × height',
      path: ['collision'],
    },
  );

// ─────────────────────────────────────────────────────────────────────────────
// V2 — CombatEntity (discriminated union by `kind`)
// ─────────────────────────────────────────────────────────────────────────────

export const EntityKindSchema = z.enum(['player', 'enemy', 'subroutine', 'neutral']);

/**
 * Per-entity AI state. Currently `z.unknown()` — narrows to per-archetype
 * shapes when AI implementations land.
 */
export const AIStateSchema = z.unknown();

/**
 * CombatEntity schema. All variants share the same shape distinguished by
 * `kind`; the reducer narrows on `kind` for variant-specific behavior.
 *
 * Position / velocity / HP fields are fixed-point integers — Zod's
 * `.int()` enforces integer values, rejecting floats from tampered saves.
 *
 * Facing is constrained to [0, 7] inclusive (8-direction sprite atlas).
 * Cooldowns and tick fields are non-negative integers.
 */
export const CombatEntitySchema = z
  .object({
    id: EntityIdSchema,
    kind: EntityKindSchema,
    archetypeId: z.string().min(1).max(128),

    posX: z.number().int(),
    posY: z.number().int(),
    velX: z.number().int(),
    velY: z.number().int(),
    facing: z.number().int().min(0).max(7),

    hpCurrent: z.number().int().nonnegative(),
    hpMax: z.number().int().positive(),
    shieldCurrent: z.number().int().nonnegative(),

    team: TeamIdSchema,

    invulnerableUntilTick: z.number().int().nonnegative(),
    stunnedUntilTick: z.number().int().nonnegative(),
    abilityCooldowns: z
      .object({
        Q: z.number().int().nonnegative(),
        E: z.number().int().nonnegative(),
        R: z.number().int().nonnegative(),
        F: z.number().int().nonnegative(),
      })
      .strict(),

    aiState: AIStateSchema.nullable(),
    deathTick: z.number().int().nonnegative().nullable(),
  })
  .strict();

// ─────────────────────────────────────────────────────────────────────────────
// V2 — Projectile
// ─────────────────────────────────────────────────────────────────────────────

export const ProjectileVisualKindSchema = z.enum(['bullet', 'bolt', 'orb', 'beam']);

/**
 * Projectile schema.
 *
 * # Limits
 *
 *   - `pierce` capped at 1024 (extreme but plausible for end-game abilities)
 *   - `hitEntityIds` capped at 1024 (matches max pierce; can't hit more
 *     entities than pierce count + 1)
 */
export const ProjectileSchema = z
  .object({
    id: ProjectileIdSchema,
    spawnedAtTick: z.number().int().nonnegative(),
    expiresAtTick: z.number().int().nonnegative(),

    posX: z.number().int(),
    posY: z.number().int(),
    velX: z.number().int(),
    velY: z.number().int(),

    ownerId: EntityIdSchema,
    team: TeamIdSchema,
    damage: z.number().int(),
    pierce: z.number().int().nonnegative().max(1024),
    hitEntityIds: z.array(EntityIdSchema).max(1024),

    visualKind: ProjectileVisualKindSchema,
  })
  .strict();

// ─────────────────────────────────────────────────────────────────────────────
// V2 — ActiveEffect
// ─────────────────────────────────────────────────────────────────────────────

export const EffectKindSchema = z.enum([
  'damage_zone',
  'heal_zone',
  'slow_field',
  'buff',
  'debuff',
]);

/**
 * Effect-specific payload. Currently `z.unknown()` — tightens per-kind
 * when effect implementations land.
 */
export const EffectPayloadSchema = z.unknown();

export const ActiveEffectSchema = z
  .object({
    id: EffectIdSchema,
    kind: EffectKindSchema,

    ownerId: EntityIdSchema.nullable(),
    targetEntityId: EntityIdSchema.nullable(),

    centerX: z.number().int(),
    centerY: z.number().int(),
    radius: z.number().int().nonnegative(),

    startedAtTick: z.number().int().nonnegative(),
    expiresAtTick: z.number().int().nonnegative(),

    tickInterval: z.number().int().positive(),
    lastAppliedTick: z.number().int().nonnegative(),

    payload: EffectPayloadSchema,
  })
  .strict();

// ─────────────────────────────────────────────────────────────────────────────
// V2 — Combat input log entry
// ─────────────────────────────────────────────────────────────────────────────

export const CombatInputEntrySchema = z
  .object({
    tick: z.number().int().nonnegative(),
    /** Action shape varies by CombatAction variant — typed as unknown for now. */
    action: z.unknown(),
  })
  .strict();

// ─────────────────────────────────────────────────────────────────────────────
// V2 — CombatState
//
// The complete shape of an active combat run. `combat: null` in GameState
// means no run is active; this schema validates the populated case.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * CombatState schema.
 *
 * # Size limits — security
 *
 *   - `entities`: capped at 4096 (Vampire Survivors-scale + headroom)
 *   - `projectiles`: capped at 4096 (every entity firing every tick is plenty)
 *   - `effects`: capped at 1024 (active AoE zones + buffs/debuffs)
 *   - `inputLog`: capped at 100000 (3+ hours of run at 30Hz, dedupe)
 *
 * These caps prevent denial-of-service via tampered saves declaring
 * billions of entities. Real combat will never approach these.
 */
export const CombatStateSchema = z
  .object({
    currentTick: z.number().int().nonnegative(),
    tileMap: TileMapSchema,
    player: CombatEntitySchema,
    entities: z.array(CombatEntitySchema).max(4096),
    projectiles: z.array(ProjectileSchema).max(4096),
    effects: z.array(ActiveEffectSchema).max(1024),
    combatRngState: RngSnapshotSchema,
    inputLog: z.array(CombatInputEntrySchema).max(100_000),
    runSeed: z.number().int(),
    architectureId: ArchitectureIdSchema,
    biomeId: BiomeIdSchema,
    startedAtTick: z.number().int().nonnegative(),
  })
  .strict()
  .refine(
    (s) => s.entities.some((e) => e.id === s.player.id),
    {
      message: 'player entity must be present in entities[] with matching id',
      path: ['player'],
    },
  );

// ─────────────────────────────────────────────────────────────────────────────
// V2 — GameState (top-level)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * V2 GameState schema. Replaces v1 turn-based combat with v2 real-time combat.
 *
 *   - `version` = 2 at write time
 *   - `combat` is `CombatStateSchema | null` (null when no run active)
 *   - `loadout` is the new player configuration field
 */
export const GameStateSchema = z
  .object({
    version: z.number().int().positive(),
    runSeed: z.number().int(),
    rng: RngSnapshotSchema,
    combat: CombatStateSchema.nullable(),
    loadout: LoadoutSchema,
  })
  .strict();

export type GameStateValidated = z.infer<typeof GameStateSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// LEGACY V1 schemas — used only by the v1→v2 migration
//
// NOT exported via the save layer's public barrel. Imported directly by
// the migration module.
// ─────────────────────────────────────────────────────────────────────────────

/** @internal V1 only. */
export const LegacyCombatantSchema = z
  .object({
    id: z.string().min(1),
    name: z.string().min(1),
    hp: z.number().int().nonnegative(),
    hpMax: z.number().int().positive(),
    attack: z.number().int().nonnegative(),
    defense: z.number().int().nonnegative(),
    initiative: z.number().int(),
  })
  .strict();

/** @internal V1 only. */
export const LegacyCombatPhaseSchema = z.enum(['idle', 'in_combat', 'victory', 'defeat']);

/** @internal V1 only. */
export const LegacyCombatStateV1Schema = z
  .object({
    phase: LegacyCombatPhaseSchema,
    allies: z.array(LegacyCombatantSchema),
    enemies: z.array(LegacyCombatantSchema),
    turn: z.number().int().nonnegative(),
    activeCombatantId: z.string().min(1).nullable(),
    log: z.array(z.string()).max(50),
  })
  .strict();

/**
 * @internal V1 only. The shape that gets fed INTO the migration; the
 * migration produces a v2 GameState that satisfies GameStateSchema.
 */
export const LegacyGameStateV1Schema = z
  .object({
    version: z.literal(1),
    runSeed: z.number().int(),
    rng: RngSnapshotSchema,
    combat: LegacyCombatStateV1Schema,
  })
  .strict();

// ─────────────────────────────────────────────────────────────────────────────
// Action log entry (unchanged from v1)
// ─────────────────────────────────────────────────────────────────────────────

export const ActionLogEntrySchema = z
  .object({
    seq: z.number().int().nonnegative(),
    timestamp: z.number().int().nonnegative(),
    action: z.unknown(),
  })
  .strict();

export type ActionLogEntry = z.infer<typeof ActionLogEntrySchema>;

// ─────────────────────────────────────────────────────────────────────────────
// UI preferences (unchanged from v1 — empty placeholder)
// ─────────────────────────────────────────────────────────────────────────────

export const UiPrefsSchema = z.object({}).strict();
export type UiPrefs = z.infer<typeof UiPrefsSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// ServerState — authoritative subset
// ─────────────────────────────────────────────────────────────────────────────

export const ServerStateSchema = z
  .object({
    gameState: GameStateSchema,
    actionLog: z.array(ActionLogEntrySchema).max(1_000_000),
  })
  .strict();

export type ServerState = z.infer<typeof ServerStateSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// LocalState — superset of ServerState plus UI prefs
// ─────────────────────────────────────────────────────────────────────────────

export const LocalStateSchema = z
  .object({
    gameState: GameStateSchema,
    actionLog: z.array(ActionLogEntrySchema).max(1_000_000),
    uiPrefs: UiPrefsSchema,
  })
  .strict();

export type LocalState = z.infer<typeof LocalStateSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// SaveEnvelope — the wire format
// ─────────────────────────────────────────────────────────────────────────────

export const ENVELOPE_VERSION = 1;

export const SaveEnvelopeSchema = z
  .object({
    envelopeVersion: z.number().int().positive(),
    createdAt: z.number().int().nonnegative(),
    installId: z.string().uuid(),
    state: LocalStateSchema,
    /** Hex-encoded HMAC-SHA256, 64 chars. */
    checksum: z.string().length(64).regex(/^[0-9a-f]{64}$/, 'must be lowercase hex SHA-256'),
  })
  .strict();

export type SaveEnvelope = z.infer<typeof SaveEnvelopeSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Pre-checksum envelope
// ─────────────────────────────────────────────────────────────────────────────

export const SaveEnvelopeForChecksumSchema = SaveEnvelopeSchema.omit({ checksum: true });
export type SaveEnvelopeForChecksum = z.infer<typeof SaveEnvelopeForChecksumSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Re-export SAVE_VERSION for save layer ergonomics
// ─────────────────────────────────────────────────────────────────────────────

export { SAVE_VERSION };
